import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import _ from 'lodash';
import { distinctUntilChanged } from 'rxjs/operators';

@Component({
    selector: 'app-autocomplete-filter',
    templateUrl: './autocomplete-filter.component.html',
    styleUrls: ['./autocomplete-filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutocompleteFilterComponent implements OnInit, OnDestroy {

    @Input() label: string;
    @Input() error: string;
    @Input() placeholder: string;
    @Input() required: boolean;
    @Input() set disabled(value: boolean | undefined) {
        if (value !== undefined) {
            if (value) {
                this.control?.disable();
            } else {
                this.control?.enable();
            }
        }
    };
    get disabled() {
        return this.control.disabled;
    }

    @Input() control: FormControl;
    protected _options: any[] | null;
    @Input() set options(value: any[] | null) {
        this._options = value;
        this.filtered = this._filter(this.control.value);
    }
    get options() {
        return this._options;
    }


    @Input() filterKey: string | string[] = '';
    @Input() displayKey: string | string[] = '';
    @Input() secondaryDisplayKey?: string | string[];
    @Input() accessor?: (option: any) => string;
    @Input() valueKey: string;
    @Input() isLoading: boolean = false;

    @Input() local_filter: boolean = true;

    @Input() appearance: 'fill' | 'outline' = 'outline';
    @Input() order: 'asc' | 'desc' | 'none' = 'none';
    @Input() local_pagination: {
        limit: number,
        skip: number,
    };
    @Input() groupBy: string;

    protected sub: Subscription = new Subscription();

    get field_disabled() {
        if (this.disabled !== undefined) {
            return this.disabled;
        } else {
            return this.control.disabled;
        }
    }

    get paginatedOptions() {
        if (this.local_pagination) {
            return this.filteredOptions.slice(this.local_pagination.skip, this.local_pagination.skip + this.local_pagination.limit);
        }
        else {
            return this.filteredOptions;
        }
    }

    get groupedOptions() {
        return _.chain(this.paginatedOptions)
            .groupBy(this.groupBy)
            .map((value, key) => {
                return { slug: key, values: value };
            })
            .value();
    }

    protected filtered: string[] | any[] = [];
    get filteredOptions(): any {
        return this.filtered; // this._filter(this.lastValue);
    };

    displayWith = (value: any) => {
        // console.log("ERROR", value, this.options, this.filteredOptions)
        if (value) {
            return typeof value === 'string' ? value : this._accessor(value, true);
        }
        else {
            // console.log("ERROR : Autocomplete no value [af]", value)
            return '';
        }
    };

    ngOnInit() {
        this.filtered = this.options || [];
        this.sub.add(this.control!.valueChanges.pipe(distinctUntilChanged())
            .subscribe((value) => {
                this.filtered = this._filter(value);
            }));

        // console.log('operateur_exe control is', this.form.control.operateur_exe.status);

        //     this.subscription.add(this.form.controls.operateur_exe.statusChanges.subscribe((status: any) => {
        //         if (status === 'DISABLED') {
        //             console.log('operateur_exe control is disabled');
        //         } else if (status === 'ENABLED') {
        //             console.log('operateur_exe control is enabled');
        //         }
        //     }));
    }

    filter() {
        if (this.local_pagination)
            this.local_pagination.skip = 0;
        if (this.control.value === '')
            this.control.setValue("");
    }

    protected _filter(value: string | any): string[] | any[] {
        let filterValue = '';
        if (typeof value === 'string')
            filterValue = value.toLowerCase();
        else
            filterValue = this._accessor(value)?.toLowerCase();
        if (this.options) {
            return this.options
                .filter(option => {
                    if(!this.local_filter) return true; // disable local filtering
                    const optionValue = typeof option === 'string' ? option : this._accessor(option);
                    return optionValue !== undefined && !_.isEmpty(optionValue) && this._compare(filterValue, optionValue);
                })
                .sort((a, b) => {
                    const getComparableValue = (option: any) => {
                        const value = typeof option === 'string' ? option : this._accessor(option);
                        return value.toLowerCase();
                    };

                    const compareStrings = (str1: string, str2: string) => {
                        return this.order === 'asc' ? str1.localeCompare(str2) : (this.order === 'desc' ? str2.localeCompare(str1) : 0);
                    };

                    const aValue = getComparableValue(a);
                    const bValue = getComparableValue(b);

                    const aStartsWith = aValue.startsWith(filterValue);
                    const bStartsWith = bValue.startsWith(filterValue);
                    const aIncludes = aValue.includes(filterValue);
                    const bIncludes = bValue.includes(filterValue);

                    // 1. Si a commence par filterValue mais pas b, a doit être avant
                    if (aStartsWith && !bStartsWith) return -1;
                    // 2. Si b commence par filterValue mais pas a, b doit être avant
                    if (!aStartsWith && bStartsWith) return 1;
                    // 3. Si a et b commencent tous les deux par filterValue, les trier par ordre alphabétique
                    if (aStartsWith && bStartsWith) return compareStrings(aValue, bValue);
                    // 4. Si aucun des deux ne commence par filterValue mais ils le contiennent, les trier alphabétiquement
                    if (aIncludes && bIncludes) return compareStrings(aValue, bValue);
                    // 5. Si a contient filterValue mais pas b, a doit être avant
                    if (aIncludes && !bIncludes) return -1;
                    // 6. Si b contient filterValue mais pas a, b doit être avant
                    if (!aIncludes && bIncludes) return 1;
                    // 7. Sinon, trier par ordre alphabétique
                    return compareStrings(aValue, bValue);
                });
        }
        else return [];
    }

    protected _compare(filterValue: string, option: string) {
        // console.log(this.label, filterValue, !_.isEmpty(filterValue), option.toLowerCase().includes(_.trim(filterValue)));
        return (!_.isEmpty(filterValue)) ? option.toLowerCase().includes(_.trim(filterValue.toLowerCase())) : true;
    }

    getLabel(option: any) {
        return _.isString(option) ? option : this._accessor(option, true);
    }

    getSecondaryLabel(option: any) {
        const key = this.secondaryDisplayKey;
        if(!key) return '';
        let v = '';
        if (_.isArray(key)) {
            key.forEach(s => v = _.join([v, _.property(s)(option)], ' '));
        } else {
            v = _.property(key)(option) as string;
        }
        return v;
    }

    protected _accessor(option: any, display: boolean = false): string {
        if (this.accessor) {
            return this.accessor(option);
        } else {
            const key = display ? this.displayKey || this.filterKey : this.filterKey;
            let v = '';
            if (_.isArray(key)) {
                key.forEach(s => v = _.join([v, _.property(s)(option)], ' '));
            } else {
                v = _.property(key)(option) as string;
            }
            return v;
        }
    }

    value(option: any) {
        // console.log("ERRORqsdfqsdfqs", option)
        if (this.valueKey) {
            return _.get(option, this.valueKey);
        } else {
            return option;
        }
    }

    onPage(event: any) {
        if (this.local_pagination) {
            this.local_pagination.skip = event.pageIndex * event.pageSize;
        }
    }

    ngOnDestroy(): void {
        this.sub.unsubscribe();
    }

    trackByItems(index: number, item: any) {
        if (item._id) return item._id;
        if (item?.name) return item.name;
        if (item?.slug) return item.slug;
        if (item?.value) return item.value;
        return item;
    }
}
